import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Navbar from './components/Navbar/Navbar';
import Profile from './components/Pages/Profile';
import Login from './components/Pages/Login';
import TokenManager from './components/Pages/TokenManager';
import MessageLogs from './components/Pages/MessageLogs';
import UniqErrorMsg from './components/Pages/UniqErrorMsg';
import ApiKeyManager from './components/Pages/ApiKeyManager';
import AlertLogs from './components/Pages/AlertLogs';
import AlertConfig from './components/Pages/AlertConfig';
import MessageReport from './components/Pages/MessageReport';
import LogoutModal from './components/Modal/LogoutModal';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
//import About from './components/Pages/About';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { AppSettingsSchema } from './schemas/appSettingsSchema';
import axios from 'axios';
import Logout from './components/Pages/Logout';
function App() {
    const [config, setConfig] = useState(null);
    const [showLogoutAlert, setShowLogoutAlert] = useState(false);
    const msalInstanceRef = useRef(null); // Store MSAL instance for modal
    const [currentUsername, setCurrentUsername] = useState(''); // Store username for modal
    const [activeAccountId, setActiveAccountId] = useState(null); // Track active account changes

    useEffect(() => {
        axios.get('/assets/appsetting.json')
            .then(response => {
                try {
                    const settings = AppSettingsSchema.parse(response.data);
                    setConfig(settings);
                } catch (error) {
                    console.error('Error parsing appsettings.json:', error);
                }
            })
            .catch(error => {
                console.error('Error loading appsettings.json:', error);
            });
    }, []);

    const userIsInRequiredGroups = async (instance, accounts, config) => {
        if (!accounts || accounts.length === 0) {
            return false; // User is not logged in
        }

        try {
            // Get the active account (the one currently selected/in use)
            const activeAccount = instance.getActiveAccount() || accounts[0];
            
            // Acquire a token with appropriate scopes to access Microsoft Graph API
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: ['https://graph.microsoft.com/.default'],
                account: activeAccount,
            });

            // Parse the token's claims to check group memberships
            const accessToken = tokenResponse.accessToken;
            const response = await fetch('https://graph.microsoft.com/v1.0/me/memberOf', {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            const data = await response.json();

            // Get the user's groups (both display names and IDs)
            const userGroups = [
                ...data.value.map(group => group.displayName),
                ...data.value.map(group => group.id)
            ];

            //Get accepted groups from config
            const acceptedGroups = config.authorisedGroups.split('|');

            //See if any of the user's groups are one of the accepted groups in config
            const found = acceptedGroups.some(acceptedGroup => userGroups.includes(acceptedGroup));
            return found;
        } catch (error) {
            console.error('Error checking group membership', error);
            return false; // Error occurred
        }
    };

    const handleLogoutConfirmation = async () => {
        setShowLogoutAlert(false); // Hide logout alert
        if (msalInstanceRef.current) {
            try {
                await msalInstanceRef.current.logoutRedirect({
                    postLogoutRedirectUri: window.location.origin + '/logout'
                });
            } catch (error) {
                // Silent error handling for logout
            }
        }
    };

    const handleSwitchAccount = async () => {
        if (msalInstanceRef.current) {
            try {
                await msalInstanceRef.current.loginRedirect({
                    prompt: 'select_account',
                    scopes: ['openid', 'profile', 'email', 'https://graph.microsoft.com/.default']
                });
            } catch (error) {
                console.error('Error in handleSwitchAccount:', error);
            }
        }
    };

    const PrivateRoute = ({ component: Component, path, config, ...rest }) => {
        const { accounts, instance, inProgress } = useMsal();
        const navigate = useNavigate();
        const [loading, setLoading] = useState(true);
        const [userInRequiredGroups, setUserInRequiredGroups] = useState(false);
        const loggingOutRef = useRef(false);  // Use ref to track logging out status

        // Store instance for modal logout
        msalInstanceRef.current = instance;

        useEffect(() => {
            const handleRedirect = async () => {
                if (inProgress === "none") {
                    // Handle redirect response (e.g., after account switch)
                    try {
                        const response = await instance.handleRedirectPromise();
                        if (response && response.account) {
                            // User just returned from account selection, set as active
                            instance.setActiveAccount(response.account);
                            // Small delay to ensure MSAL state is fully updated
                            setTimeout(() => {
                                // Trigger React state update to force permission recheck
                                setActiveAccountId(response.account.homeAccountId);
                            }, 100);
                        }
                    } catch (error) {
                        // Handle redirect errors silently
                    }

                    if (accounts.length === 0) {
                        const returnUrl = path;
                        // User is not authenticated, redirect to the login page with returnUrl
                        const loginUrl = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
                        navigate(loginUrl, { replace: true });
                    } else if (accounts.length > 0 && !instance.getActiveAccount()) {
                        // If we have accounts but no active account set, set the first one
                        instance.setActiveAccount(accounts[0]);
                    }
                }
            };

            handleRedirect();
        }, [accounts, inProgress, navigate, path, instance]);

        useEffect(() => {
            const checkUserGroups = async () => {
                setLoading(true);
                try {
                    const result = await userIsInRequiredGroups(instance, accounts, config);
                    setUserInRequiredGroups(result);
                } catch (error) {
                    setUserInRequiredGroups(false);
                } finally {
                    setLoading(false);
                }
            };

            // Update username when accounts change
            if (accounts.length > 0) {
                // Get the active account (the one currently selected/in use)
                const activeAccount = instance.getActiveAccount() || accounts[0];
                const username = activeAccount?.username || activeAccount?.name || 'Unknown User';
                setCurrentUsername(username);
                loggingOutRef.current = false;
                checkUserGroups();
            }
        }, [instance, accounts, config, activeAccountId]); // Added activeAccountId to dependencies

        useEffect(() => {
            // Show logout alert when user lacks permissions
            if (!loading && accounts.length > 0 && inProgress === "none" && !userInRequiredGroups && !loggingOutRef.current) {
                loggingOutRef.current = true;
                setShowLogoutAlert(true);
            }
            // Hide modal when user has correct permissions
            if (!loading && accounts.length > 0 && inProgress === "none" && userInRequiredGroups) {
                setShowLogoutAlert(false);
                loggingOutRef.current = false; // Allow modal to show again if user switches to invalid account
            }
        }, [loading, accounts.length, inProgress, userInRequiredGroups]);

        if (loading) {
            return <LoadingSpinner message="Loading..." />;
        }

        if (showLogoutAlert) {
            return null;
        }

        const handleLogout = async () => {
            try {
                await instance.logoutRedirect();
            } catch (error) {
                // Silent error handling for logout
            }
        };

        if (accounts.length > 0 && inProgress === "none" && userInRequiredGroups) {
            return <Component {...rest} config={config} activeUsername={currentUsername} />;
        } else if (accounts.length > 0 && inProgress === "none" && !userInRequiredGroups) {
            return null;
        } else {
            return null;
        }
    };
    
    if (config === null) {
        return <LoadingSpinner message="Loading configuration..." />;
    }

    return (
        <div className="App">
            <Router>
                <div>
                    <Navbar
                        config={config}
                    />
                    {showLogoutAlert && (
                        <LogoutModal onClose={() => setShowLogoutAlert(false)} ariaLabel="Access denied notification">
                            <h2 id="logout-modal-title" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Access Denied</h2>
                            <p id="logout-modal-description">
                                Access has not been granted because <strong>{currentUsername}</strong> does not have one of the valid AD Groups.
                            </p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                                <button 
                                    onClick={handleSwitchAccount}
                                    aria-label="Switch to a different account"
                                    style={{ 
                                        backgroundColor: '#1976d2', 
                                        color: 'white', 
                                        border: 'none', 
                                        padding: '10px 20px', 
                                        borderRadius: '4px', 
                                        cursor: 'pointer' 
                                    }}
                                >
                                    Switch Account
                                </button>
                                <button 
                                    onClick={handleLogoutConfirmation}
                                    aria-label="Logout from the application"
                                    style={{ 
                                        backgroundColor: '#d32f2f', 
                                        color: 'white', 
                                        border: 'none', 
                                        padding: '10px 20px', 
                                        borderRadius: '4px', 
                                        cursor: 'pointer' 
                                    }}
                                >
                                    Logout
                                </button>
                            </div>
                        </LogoutModal>
                    )}
                    <main id="main-content" tabIndex="-1">
                    <Routes>
                        <Route path="/" element={
                            <PrivateRoute path="/messagelogs" component={MessageLogs} config={config} />} />

                        <Route path="/profile" element={
                            <PrivateRoute path="/profile" component={Profile} config={config} />} />
                        <Route path="/messagelogs" element={
                            <PrivateRoute path="/messagelogs" component={MessageLogs} config={config} />} />
                        <Route path="/uniqerrormsg" element={
                            <PrivateRoute path="/uniqerrormsg" component={UniqErrorMsg} config={config} />} />
                        <Route path="/apikeymanager" element={
                            <PrivateRoute path="/apikeymanager" component={ApiKeyManager} config={config} />} />
                        <Route path="/alertlogs" element={
                            <PrivateRoute path="/alertlogs" component={AlertLogs} config={config} />} />
                        <Route path="/alertconfig" element={
                            <PrivateRoute path="/alertconfig" component={AlertConfig} config={config} />} />
                        <Route path="/messagereport" element={
                            <PrivateRoute path="/messagereport" component={MessageReport} config={config} />} />
                        <Route path="/tokenmanager" element={
                            <PrivateRoute path="/tokenmanager" component={TokenManager} config={config} />} />
                        {/*<Route path="/about" element={*/}
                        {/*    <PrivateRoute path="/about" component={About} config={config} />} />*/}


                        <Route path="/login" element={<Login />} />
                        <Route path="/logout" element={<Logout />} />
                    </Routes>
                    </main>
                </div>
            </Router>
        </div>
    );
}

export default App;
